import { Button, createTableColumn, makeStyles, TableCellLayout, TableColumnDefinition } from '@fluentui/react-components';
import { TableColumnSizingOptions } from '@fluentui/react-components';
import React from 'react';
import { Outlet } from 'react-router-dom';
import { VentLog, ventTime } from '../model/VentLog';
import { ItemsListProps, ItemsListWithoutStyles, ItemsListWithStyles } from './ItemsList';
import { SiteHeader } from './SiteHeader';
import { withStyles } from "./withStyles";
import { Smoke } from "../model/Smoke";
import { AddOrUpdateDelegate, DeleteDelegate } from "../model/ItemDelegates";
import { EditRegular, DeleteRegular } from "@fluentui/react-icons";
import { ModalPrompt } from '../dialogs/ModalPrompt';
import { RexDateTime } from "../Dates/RexDateTime";
import { EditAddVentLog } from '../dialogs/EditAddVentLog';

export interface VentLogsListProps
{
    styles: Record<string, string>;
    ventLogs: VentLog[];
    smoke: Smoke;
    onDelete?: DeleteDelegate<VentLog>;
    onCommit?: AddOrUpdateDelegate<VentLog>;
}

export interface VentLogsListState
{
    isEditing: boolean;
    isAdding: boolean;
    itemEditing?: VentLog;
    isConfirmingDelete: boolean;
    itemDeleting?: VentLog;
}

interface VentLogsListItemArgs
{
}

const useStyles = makeStyles(
    {
    });

const VentLogsListItemsList: React.ComponentType<ItemsListProps<VentLog, VentLogsListItemArgs>> =
    ItemsListWithStyles<VentLog, VentLogsListItemArgs>(ItemsListWithoutStyles < VentLog, VentLogsListItemArgs > );

export class VentLogsListWithoutStyles extends React.Component<VentLogsListProps, VentLogsListState>
{
    constructor(props: VentLogsListProps)
    {
        super(props);
        this.state =
        {
            isEditing: false,
            isAdding: false,
            itemEditing: new VentLog(),
            isConfirmingDelete: false,
            itemDeleting: undefined
        };
    }

    startEdit(item: VentLog)
    {
        this.setState(
            {
                isEditing: true,
                isAdding: false,
                itemEditing: item
            });
    }

    onEditButtonClick(event: React.MouseEvent<HTMLButtonElement>, item: VentLog)
    {
        this.startEdit(item);
        event.stopPropagation();
    }

    startAdd()
    {
        const newItem = new VentLog();
        newItem.SmokeID = this.props.smoke.SmokeID;

        this.setState(
            {
                isEditing: true,
                isAdding: true,
                itemEditing: newItem
            });
    }

    onDeleteButtonClick(event: React.MouseEvent<HTMLButtonElement>, item: VentLog)
    {
        this.setState(
            {
                isConfirmingDelete: true,
                itemDeleting: item
            });
        event.stopPropagation();
    }

    async onDeleteConfirmed()
    {
        if (!this.props.onDelete)
            throw new Error("onDelete not set");

        await this.props.onDelete(this.state.itemDeleting!);

        this.setState(this.stateResetDialogs());
    }

    async onSaveEditDialog(item: VentLog)
    {
        if (!this.props.onCommit)
            throw new Error("onCommit not set");

        await this.props.onCommit(item, this.state.isAdding);
        this.setState(this.stateResetDialogs());
    }

    stateResetDialogs()
    {
        return {
            isEditing: false,
            isAdding: false,
            isConfirmingDelete: false,
            itemEditing: undefined,
            itemDeleting: undefined
        };
    }

    onDismissDialogs()
    {
        this.setState(this.stateResetDialogs());
    }

    getTableColumnDefinitions()
    {
        const columns: TableColumnDefinition<VentLog>[] =
        [
            createTableColumn<VentLog>(
                {
                    columnId: "Time",
                    compare: (left, right) => ventTime(left).localeCompare(ventTime(right)),
                    renderHeaderCell: () => { return "Time"; },
                    renderCell: (item: VentLog) =>
                    {
                        return (
                            <TableCellLayout>{ventTime(item)}</TableCellLayout>);
                    }
                }),
            createTableColumn<VentLog>(
                {
                    columnId: "vent1",
                    compare: (left, right) => left.Vent1 - right.Vent1,
                    renderHeaderCell: () => { return "Vent1"; },
                    renderCell: (item: VentLog) =>
                    {
                        return (
                            <TableCellLayout>{item.Vent1}</TableCellLayout>);
                    }
                }),
            createTableColumn<VentLog>(
                {
                    columnId: "vent2",
                    compare: (left, right) => left.Vent2 - right.Vent2,
                    renderHeaderCell: () => { return "Vent2"; },
                    renderCell: (item: VentLog) =>
                    {
                        return (
                            <TableCellLayout>{item.Vent2}</TableCellLayout>);
                    }
                }),
            createTableColumn<VentLog>(
                {
                    columnId: "vent3",
                    compare: (left, right) => left.Vent3 - right.Vent3,
                    renderHeaderCell: () => { return "Vent3"; },
                    renderCell: (item: VentLog) =>
                    {
                        return (
                            <TableCellLayout>{item.Vent3}</TableCellLayout>);
                    }
                }),
            createTableColumn<VentLog>(
                {
                    columnId: "comment",
                    compare: (left, right) => left.Comment.localeCompare(right.Comment),
                    renderHeaderCell: () => { return "Comment"; },
                    renderCell: (item: VentLog) =>
                    {
                        return (
                            <TableCellLayout>{item.Comment}</TableCellLayout>);
                    }
                }),
            createTableColumn<VentLog>(
                {
                    columnId: "actions",
                    renderHeaderCell: () => { return "Actions"; },
                    renderCell: (item: VentLog) =>
                    {
                        return (
                            <span style={{whiteSpace: 'nowrap'}}>
                                <Button aria-label="Edit" icon={<EditRegular/>} onClick={(event) => this.onEditButtonClick(event, item)}/>
                                <Button aria-label="Delete" icon={<DeleteRegular/>} onClick={(event) => this.onDeleteButtonClick(event, item)}/>
                            </span>
                        );
                    }
                }),
        ];

        return columns;
    }

    render()
    {
        const getRowId = (item: VentLog) => `${(item.Timestamp as RexDateTime).utcValue}`;
        const onEditButtonClicked = (item: VentLog, args: VentLogsListItemArgs) => {}
        const columns = this.getTableColumnDefinitions();
        const canEditItem = (item: VentLog) => true;
        const onDismiss = this.onDismissDialogs.bind(this);
        const onDeleteConfirmed = this.onDeleteConfirmed.bind(this);

        const tableColumnSizingOptions = { autoFitColumns: true };

        const editAddDialog = (
            <EditAddVentLog
                open={this.state.isEditing}
                itemEditing={this.state.itemEditing}
                isAdd={this.state.isAdding}
                visible={false}
                onDismiss={onDismiss}
                onSave={this.onSaveEditDialog.bind(this)}/>);

        const confirmDeleteDialog = (
            <ModalPrompt title="Delete VentLog?" confirm="Delete" onDismiss={onDismiss} onSave={onDeleteConfirmed} open={this.state.isConfirmingDelete} visible={false}>
                Are you sure you want to delete the ventlog at timestamp {(this.state.itemDeleting?.Timestamp as RexDateTime)?.toLocalDateTimeString()} for smoke {this.props.smoke?.Description}?
            </ModalPrompt>);

        return (
            <div>
                <VentLogsListItemsList
                    editArgs={{}}
                    getRowId={getRowId}
                    items={this.props.ventLogs}
                    columns={columns}
                    canEditItem={canEditItem}
                    onEditItem={onEditButtonClicked}
                    autofitContents={true}/>

                <Button onClick={() => this.startAdd()}>Add</Button>

                {this.state.isEditing && editAddDialog}
                {this.state.isConfirmingDelete && confirmDeleteDialog}
            </div>
        );
    }
}

export const VentLogsList = withStyles(useStyles, VentLogsListWithoutStyles);